import { Button } from '@wheely/ui-kit';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { usePermissions } from '@/app/hooks/usePermissions';
import { IntlContext } from '@/i18n';
import { deleteCar } from '@/modules/car/actions/async/deleteCar';
import { prepareCarForm } from '@/modules/car/actions/async/prepare-car-form';
import { prepareUploadCarForm } from '@/modules/car/actions/async/prepare-upload-car-form';
import { reloadFormForCurrentCar } from '@/modules/car/actions/async/reload-form-for-current-car';
import { WHEELBASES_MESSAGES, isWheelbase } from '@/modules/car/api/hardcoded-make-models';
import { formatEngine } from '@/modules/car/engine';
import { useFormOptions } from '@/modules/car/hooks/useFormOptions';
import { CarDocType } from '@/modules/car/model';
import type { VehicleUpdateForm } from '@/modules/car/model/car-form';
import { getHasRequiredCarDocs } from '@/modules/car/store/commonSelectors';
import { getCarForm, getCurrentCar, getCurrentCarDriver } from '@/modules/car/store/selectors';
import { carUploadSelectors } from '@/modules/car/store/upload-slice';
import { getMyDriver } from '@/modules/driver/store/selectors';
import { getPartner } from '@/modules/partner/store/selectors';
import { selectIsIndividualChauffeur } from '@/modules/user/store/selectors';
import { AppDispatch } from '@/root-store';
import { AccessStatusEnum, TAccessStatus } from '@/shared/models/access-status';
import { CountryCode, type TCountryCodes } from '@/shared/models/country-code';
import { AccessStatus } from '@/shared/view/AccessStatus';
import { DescriptionList } from '@/shared/view/DescriptionList';
import { StepLayout } from '@/shared/view/StepLayout';
import { UploadCar } from '@/shared/view/Upload';
import { useCollapsedDocsList } from '@/shared/view/Upload/SupplementaryDocs/useCollapsedDocsList';
import { DocsList, HandleDocListItemClick } from '@src-common/view/components/DocsList';

import { createBookCarAppointmentCard } from '../Todo/hooks/createBookCarAppointmentCard';
import { createGBBookCarAppointmentCard } from '../Todo/hooks/createGBBookCarAppointmentCard';
import { ToDoView } from '../Todo/view';
import { messages as vehicleMessages } from '../VehicleCreate/messages';

import { CurrentChauffeur } from './components/CurrentChauffeur';
import s from './styles.module.css';

const mapToCarInfo = (
  {
    wheelbase,
    color,
    interiorColor,
    year,
    registrationDate,
    licensePlate,
    licensingAuthority,
    licenseNumber,
    generation,
    vin,
    engineType,
  }: VehicleUpdateForm & { accessStatus: TAccessStatus },
  intl: IntlShape,
  region: TCountryCodes,
) => ({
  wheelbase: isWheelbase(wheelbase)
    ? intl.formatMessage(WHEELBASES_MESSAGES[wheelbase])
    : wheelbase,
  engineType: formatEngine(engineType, intl, region),
  color,
  interiorColor,
  year,
  registrationDate,
  licensePlate,
  licensingAuthority,
  PHV: licenseNumber,
  tlcVehicleLicenseNumber: licenseNumber,
  generation,
  vin,
});

type TTitleLine = [keyof ReturnType<typeof mapToCarInfo>, string];

const skipEmpty = (item: TTitleLine | false): item is TTitleLine => item !== false;

interface IProps {
  carId: string;
  onNavigateBack: (uri: string) => void;
}

const CarDetails: React.FC<IProps> = ({ carId, onNavigateBack }) => {
  const dispatch = useDispatch() as AppDispatch;

  usePermissions('viewCars');

  useEffect(() => {
    dispatch(prepareCarForm({ carId }));
  }, [carId, dispatch]);

  const { region } = useContext(IntlContext);
  const intl = useIntl();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const carHasRequiredDocs = useSelector(getHasRequiredCarDocs);

  const titles: Array<TTitleLine> = useMemo(() => {
    const allTitles: Array<TTitleLine | false> = [
      [
        'generation',
        intl.formatMessage({
          defaultMessage: 'Generation',
          id: '2jSkNX',
          description: 'Vehicle info: Generation field title',
        }),
      ],
      [
        'color',
        intl.formatMessage({
          defaultMessage: 'Vehicle color',
          id: 'VLD5bd',
          description: 'Car screen: list title',
        }),
      ],
      [
        'interiorColor',
        intl.formatMessage({
          defaultMessage: 'Interior Color',
          id: 'GvoEXn',
          description: 'Car screen: list title',
        }),
      ],
      [
        'year',
        intl.formatMessage({
          defaultMessage: 'Year',
          id: '4imqJB',
          description: 'Car screen: list title',
        }),
      ],
      [
        'registrationDate',
        intl.formatMessage({
          defaultMessage: 'Date of first registration',
          id: 'hAkYqw',
          description: 'Car screen: list title',
        }),
      ],
      [
        'licensePlate',
        intl.formatMessage({
          defaultMessage: 'Licence Plate',
          id: '7qlE2U',
          description: 'Car screen: list title',
        }),
      ],
      [
        'vin',
        intl.formatMessage({
          defaultMessage: 'VIN',
          id: 'H/UHUa',
          description: 'Car screen: list title',
        }),
      ],
      region === CountryCode.UnitedStates && [
        'tlcVehicleLicenseNumber',
        intl.formatMessage(vehicleMessages.tlcVehicleLicense),
      ],
      [
        'engineType',
        intl.formatMessage({
          defaultMessage: 'Engine',
          id: 'global_vehicle_info_engine',
          description: 'Car screen: list title',
        }),
      ],
      [
        'wheelbase',
        intl.formatMessage({
          defaultMessage: 'Wheelbase',
          id: 'F2299T',
          description: 'Car screen: list title',
        }),
      ],
      region === CountryCode.GreatBritain && [
        'licensingAuthority',
        intl.formatMessage({
          defaultMessage: 'Licensing authority',
          id: 'fdwPip',
          description: 'Car screen: list title',
        }),
      ],
      region === CountryCode.GreatBritain && [
        'PHV',
        intl.formatMessage({
          defaultMessage: 'PHV',
          id: '6Xhtjk',
          description: 'Car screen: list title',
        }),
      ],
    ];

    return allTitles.filter(skipEmpty);
  }, [intl, region]);

  const handleDeleteCar = useCallback(async () => {
    setIsDeleting(true);

    try {
      await dispatch(deleteCar({ carId, onSuccess: () => onNavigateBack('/todo') })).unwrap();
    } finally {
      setIsDeleting(false);
    }
  }, [carId, dispatch, onNavigateBack]);

  const [isUploading, setIsUploading] = useState(false);

  const formValues = useSelector(getCarForm);
  const { colors, interiorColors, wheelbases, engines } = useFormOptions(
    formValues.make,
    formValues.model,
  );
  const values = mapToCarInfo(formValues, intl, region);
  const docs = useCollapsedDocsList(useSelector(carUploadSelectors.getDocsList), 'car');
  const isIndividualChauffeur = useSelector(selectIsIndividualChauffeur);
  const partner = useSelector(getPartner);
  const myDriver = useSelector(getMyDriver);
  const car = useSelector(getCurrentCar);
  const carDriver = useSelector(getCurrentCarDriver);

  const handleClickDocListItem = useCallback<HandleDocListItemClick>(
    (_, doc) => {
      dispatch(
        prepareUploadCarForm({
          docType: doc.docType as CarDocType,
          onSuccess: () => setIsUploading(true),
        }),
      );
    },
    [dispatch, setIsUploading],
  );

  const handleCancelUploading = useCallback(() => {
    setIsUploading(false);
  }, []);

  const handleUploaded = useCallback(() => dispatch(reloadFormForCurrentCar({})), [dispatch]);

  const handleFinishUploading = useCallback(() => {
    setIsUploading(false);
  }, []);

  const handleClickBack = useCallback(async () => {
    onNavigateBack('/todo');
  }, [onNavigateBack]);

  const links = useExternalLinks();

  return (
    <>
      <StepLayout
        onClickBack={handleClickBack}
        title={
          <>
            <div>{`${formValues.licensePlate} ${formValues.make} ${formValues.model}`}</div>
            <AccessStatus status={formValues.accessStatus} />
          </>
        }
        hideProgress={true}
      >
        <div>
          <DescriptionList
            title={intl.formatMessage({
              defaultMessage: 'Information',
              id: 'G9nZ5f',
              description: 'Car screen subheader',
            })}
            itemsForSelect={{
              color: colors,
              interiorColor: interiorColors,
              wheelbase: wheelbases,
              engineType: engines,
            }}
            className={s.values}
            values={values}
            titles={titles}
            action={
              region === CountryCode.GreatBritain && (
                <Link to={`/vehicles/${carId}/edit`} className={s.edit}>
                  {intl.formatMessage({
                    defaultMessage: 'Edit',
                    id: 'global_action_edit',
                    description: 'Profile screen: button text',
                  })}
                </Link>
              )
            }
          />
          {carDriver && <CurrentChauffeur chauffeur={carDriver} />}
          {isIndividualChauffeur &&
            partner.accessStatus === 'active' &&
            partner.countryCode === CountryCode.GreatBritain &&
            myDriver?.accessStatus !== 'pending' &&
            car &&
            car.accessStatus !== AccessStatusEnum.active && (
              <div className={s.todo}>
                <ToDoView cards={[createGBBookCarAppointmentCard(intl)]} />
              </div>
            )}
          {partner.countryCode !== CountryCode.GreatBritain && carHasRequiredDocs && (
            <div className={s.todo}>
              <ToDoView cards={[createBookCarAppointmentCard(intl, links)]} />
            </div>
          )}
          <DocsList
            title={intl.formatMessage({
              defaultMessage: 'DOCUMENTS',
              id: 'wVBktP',
              description: 'Docs list title',
            })}
            className={s.docs}
            onChange={handleClickDocListItem}
            docs={docs}
          />
        </div>
        <div className={s.actions}>
          <Button
            minimal={true}
            intent="danger"
            className={s.deleteButton}
            onClick={handleDeleteCar}
            data-test-id="delete-vehicle"
            disabled={isDeleting}
          >
            <FormattedMessage
              defaultMessage="Delete vehicle"
              id="UJmA/e"
              description="Car screen: action button"
            />
          </Button>
        </div>
      </StepLayout>
      {isUploading && (
        <UploadCar
          onUploaded={handleUploaded}
          onFinishUpload={handleFinishUploading}
          onClickSkip={handleCancelUploading}
        />
      )}
    </>
  );
};

export { CarDetails };
